import {Link, NavLink} from "react-router-dom";
import {SiFacebook, SiInstagram, SiTiktok, SiX} from "@icons-pack/react-simple-icons";
import {isDropdownNode, type NavItem, type NavLinks} from "./Navbar"; // or move types to a shared file
import {APP_BUILD, APP_VERSION} from "../lib/version";

type Props = {
    logo: string;
    links: NavLinks; // uses links.main, links.legal
    navClass?: ({isActive}: { isActive: boolean }) => string;
};

const cx = (...classes: Array<string | false | null | undefined>) =>
    classes.filter(Boolean).join(" ");

const isExternal = (to: string) => /^https?:\/\//i.test(to);

const defaultNavClass = ({isActive}: { isActive: boolean }) =>
    cx(
        "text-lg/6 transition",
        isActive ? "text-orange-700 font-bold" : "text-white hover:text-orange-700"
    );

export function Footer({
                           logo,
                           links,
                           navClass = defaultNavClass,
                       }: Props) {
    const main = (links.main ?? []).filter((node): node is NavItem => !isDropdownNode(node));
    const legal = links.legal ?? [];
    const social = links.social ?? {};

    const socialLinks = [
        {href: social.instagram, label: "Instagram", Icon: SiInstagram},
        {href: social.facebook, label: "Facebook", Icon: SiFacebook},
        {href: social.tiktok, label: "TikTok", Icon: SiTiktok},
        {href: social.x, label: "X", Icon: SiX},
    ].filter((s): s is { href: string; label: string; Icon: typeof SiInstagram } => Boolean(s.href));

    const half = Math.ceil(main.length / 2);
    const leftLinks = main.slice(0, half);
    const rightLinks = main.slice(half);

    return (
        <footer className="bg-zinc-950">
            <div className="mx-auto max-w-7xl px-6 pt-16 pb-8 sm:pt-18 lg:px-8 lg:pt-22">
                <div className="xl:grid xl:grid-cols-3 xl:gap-8">
                    <div className="space-y-8">
                        <Link to="/" className="-m-1.5 p-1.5 inline-flex items-center">
                            <span className="sr-only">NamEvents</span>
                            <img alt="NamEvents" src={logo} className="h-20 w-auto invert"/>
                        </Link>

                        {socialLinks.length > 0 && (
                            <div>
                                <h3 className="text-sm/6 font-semibold text-white">Follow us</h3>
                                <ul role="list" className="mt-6 flex flex-wrap gap-3">
                                    {socialLinks.map(({href, label, Icon}) => (
                                        <li key={label}>
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noreferrer"
                                                aria-label={label}
                                                className="group inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium text-white ring-1 ring-white/20 backdrop-blur transition hover:bg-white/20"
                                            >
                                                <span className="flex size-6 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15 group-hover:bg-orange-500/30">
                                                    <Icon className="size-3.5 text-white" aria-hidden="true"/>
                                                </span>
                                                <span>{label}</span>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
                        {/* Quick links (main split into 2 columns) */}
                        <div className="md:grid md:grid-cols-2 md:gap-8">
                            <div>
                                <h3 className="text-sm/6 font-semibold text-white">Quick Links</h3>
                                <ul role="list" className="mt-6 space-y-4">
                                    {leftLinks.map((item) => (
                                        <li key={item.label}>
                                            {isExternal(item.to) ? (
                                                <a
                                                    href={item.to}
                                                    target={item.target ?? "_blank"}
                                                    rel="noreferrer"
                                                    className={item.className}
                                                >
                                                    {item.label}
                                                </a>
                                            ) : (
                                                <NavLink
                                                    to={item.to}
                                                    className={({isActive}) =>
                                                        [navClass ? navClass({isActive}) : "", item.className ?? ""].join(" ")
                                                    }
                                                >
                                                    {item.label}
                                                </NavLink>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="mt-10 md:mt-0">
                                <h3 className="text-sm/6 font-semibold text-white">Resources</h3>
                                <ul role="list" className="mt-6 space-y-4">
                                    {rightLinks.map((item) => (
                                        <li key={item.label}>
                                            {isExternal(item.to) ? (
                                                <a
                                                    href={item.to}
                                                    target={item.target ?? "_blank"}
                                                    rel="noreferrer"
                                                    className={item.className}
                                                >
                                                    {item.label}
                                                </a>
                                            ) : (
                                                <NavLink
                                                    to={item.to}
                                                    className={({isActive}) =>
                                                        [navClass ? navClass({isActive}) : "", item.className ?? ""].join(" ")
                                                    }
                                                >
                                                    {item.label}
                                                </NavLink>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Legal */}
                        <div className="md:grid md:grid-cols-2 md:gap-8">
                            <div className="mt-10 md:mt-0">
                                <h3 className="text-sm/6 font-semibold text-white">Legal</h3>
                                <ul role="list" className="mt-6 space-y-4">
                                    {legal.map((item) => (
                                        <li key={item.label}>
                                            <NavLink to={item.to} className={navClass}>
                                                {item.label}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-16 border-t border-gray-900/10 pt-8 sm:mt-20 lg:mt-24 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-sm/6 text-white">
                        &copy; {new Date().getFullYear()} NamEvents. All rights reserved.{" "}
                        <span className="text-gray-500" title={APP_BUILD}>
                            v{APP_VERSION}
                        </span>
                    </p>
                    <p className="text-sm/6 text-gray-500">
                        Developed by{" "}
                        <a
                            href="https://www.pct-ventures.com/"
                            target="_blank"
                            rel="noreferrer"
                            className="text-white hover:text-orange-700 transition"
                        >
                            PCT Ventures
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
}